import axios from 'axios'
import auth from '@api/auth'
import { europeanTimezones } from '@meta/countries'

import {
  CACHE_USER,
  UPDATE_BEGIN,
  UPDATE_CLEAR,
  UPDATE_FAILURE,
  UPDATE_SUCCESS,
  GET_DETAILS_SUCCESS,
  GET_DETAILS_FAILURE,
  ACCOUNT_CLEAR,
  SESSION_EXPIRE,
} from './_types'
import getErrorMessage from '@utils/getErrorMessage'

const getDefaultState = () => {
  return {
    cached: [],
    currentUser: {
      id: '',
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      timezone: '',
      isConfirmed: '',
      hasSocialLogin: '',
      hasUpdatedPassword: '',
      apiKey: '',
      subscription: {
        id: '',
        braintreeCustomerId: '',
        braintreeSubscriptionId: '',
        lastFourDigits: '',
        firstBillingDate: '',
        expiresAt: '',
        user: '',
        features: [],
        name: '',
        price: '',
        billingPeriod: '',
        currency: '',
        maxCampaigns: 0,
        maxDomains: 0,
        maxPageViews: 0,
      },
    },
    getUserCompleted: false,
    updateCompleted: false,
    updateError: false,
    updateLoading: false,
    updateErrorMessage: '',
    userSessionExpired: false,
  }
}
export const state = getDefaultState()

export const getters = {
  allowSetPassword: (state) => state.currentUser.hasSocialLogin && !state.currentUser.hasUpdatedPassword,
  hasPaidSubscription: (state) => state.currentUser.subscription.id !== 'basic',
  hasCanceledSubscription: (state) => state.currentUser.subscription.cancelsAt !== null,
  defaultCurrency: (state) => europeanTimezones.includes(state.currentUser.timezone) ? 'eur' : 'usd',
}

export const mutations = {
  [CACHE_USER](state, newUser) {
    state.cached.push(newUser)
  },
  [GET_DETAILS_SUCCESS](state, data) {
    state.currentUser = data
    state.getUserCompleted = true
  },
  [GET_DETAILS_FAILURE](state) {
    state.getUserCompleted = false
  },
  [UPDATE_BEGIN](state) {
    state.updateLoading = true
    state.updateCompleted = false
    state.updateErrorMessage = ''
  },
  [UPDATE_CLEAR](state) {
    state.updateCompleted = false
    state.updateError = false
    state.updateLoading = false
  },
  [UPDATE_FAILURE](state, message) {
    state.updateError = true
    state.updateLoading = false
    state.updateErrorMessage = message
  },
  [UPDATE_SUCCESS](state, data) {
    state.updateCompleted = true
    state.updateError = false
    state.updateLoading = false
    state.currentUser.username = data.username
    state.currentUser.email = data.email
    state.currentUser.firstName = data.firstName
    state.currentUser.lastName = data.lastName
  },
  [ACCOUNT_CLEAR](state) {
    Object.assign(state, getDefaultState())
  },
  [SESSION_EXPIRE](state) {
    state.userSessionExpired = true
    for (const key in state.currentUser) {
      state.currentUser[key] = null
    }
  },
}

export const actions = {
  fetchUser({ commit, state, rootState }, { username }) {
    // 1. Check if we already have the user as a current user.
    const { currentUser } = rootState.auth
    if (currentUser && currentUser.username === username) {
      return Promise.resolve(currentUser)
    }

    // 2. Check if we've already fetched and cached the user.
    const matchedUser = state.cached.find((user) => user.username === username)
    if (matchedUser) {
      return Promise.resolve(matchedUser)
    }

    // 3. Fetch the user from the API and cache it in case
    //    we need it again in the future.
    return axios.get(`/api/users/${username}`).then((response) => {
      const user = response.data
      commit('CACHE_USER', user)
      return user
    })
  },
  getAccountDetails({ commit }) {
    return auth.getAccountDetails().then((response) => {
      const user = response.data
      commit(GET_DETAILS_SUCCESS, user)
      return user
    }).catch((error) => {
      commit(GET_DETAILS_FAILURE)
      const errorMessage = getErrorMessage(error)
      commit(GET_DETAILS_FAILURE, errorMessage)
      Promise.reject(error)
    })
  },
  updateAccountDetails({ commit }, data) {
    commit(UPDATE_BEGIN)

    return auth.updateAccountDetails(data).
      then((response) => commit(UPDATE_SUCCESS, response.data)).
      catch(() => commit(UPDATE_FAILURE))
  },
  clearUpdateAccountStatus({ commit }) {
    commit(UPDATE_CLEAR)
  },
  clearState({ commit }) {
    commit(ACCOUNT_CLEAR)
  },
}
